import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaTimes } from 'react-icons/fa';

const products = [
  {
    id: 1,
    name: 'Solitaire Ring',
    image: 'https://d3d71ba2asa5oz.cloudfront.net/53000589/images/0100y.jpg',
    description: 'A classic solitaire diamond ring set in 18K white gold. Features a brilliant round-cut diamond with excellent clarity.',
    material: '18K White Gold',
    craftsmanship: 'Certified Diamond'
  },
  {
    id: 2,
    name: 'Halo Ring',
    image: 'https://tse4.mm.bing.net/th/id/OIP.oaiFTNDBDB3Rp6EKP-qwkwHaHa?rs=1&pid=ImgDetMain&o=7&rm=3',
    description: 'Elegant halo setting with a center diamond surrounded by smaller diamonds. Crafted in 14K rose gold for a romantic look.',
    material: '14K Rose Gold',
    craftsmanship: 'Pave Setting'
  },
  {
    id: 3,
    name: 'Three-Stone Ring',
    image: 'https://tse3.mm.bing.net/th/id/OIP.yqkl4fblGA_YEBx4KNQiNgHaHa?w=1000&h=1000&rs=1&pid=ImgDetMain&o=7&rm=3',
    description: 'Symbolizing past, present, and future, this three-stone ring features three matched diamonds in a platinum band.',
    material: 'Platinum',
    craftsmanship: 'Prong Setting'
  },
  {
    id: 4,
    name: 'Vintage Ring',
    image: 'https://cdn11.bigcommerce.com/s-jnlnq3j8vo/images/stencil/original/products/10784/25354/platinum-pierced-design-vintage-engagement-ring__81682.1649707652.jpg?c=1',
    description: 'Intricate vintage-inspired design with milgrain details and a stunning old European cut diamond. Set in 18K yellow gold.',
    material: '18K Yellow Gold',
    craftsmanship: 'Hand Engraved'
  },
  {
    id: 5,
    name: 'Eternity Band',
    image: 'https://product-images.therealreal.com/MAC31535_1_enlarged.jpg',
    description: 'Full circle diamond eternity band, perfect for anniversaries or stacking. Diamonds set in 14K white gold.',
    material: '14K White Gold',
    craftsmanship: 'Channel Setting'
  },
  {
    id: 6,
    name: 'Pear-Shaped Ring',
    image: 'https://i.etsystatic.com/24208257/r/il/0a6e6e/3350450790/il_794xN.3350450790_6bgq.jpg',
    description: 'Unique pear-shaped diamond in a delicate halo setting. The tapered band adds elegance to this contemporary design.',
    material: '18K Rose Gold',
    craftsmanship: 'Micro Pave'
  },
];

const Rings = () => {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [failedImages, setFailedImages] = useState({});
  const modalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setSelectedProduct(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleImageError = (id) => {
    setFailedImages((prev) => ({ ...prev, [id]: true }));
  };

  const handleViewDetails = (product) => {
    setSelectedProduct(product);
  };

  return (
    <div className="min-h-screen bg-white text-stone-800 p-8">
      <div className="text-sm text-stone-500 mb-4">
        <Link to="/shop" className="hover:text-amber-600 transition-colors">Shop</Link> / <span className="text-stone-800 font-medium">Rings</span>
      </div>

      <h1 className="text-4xl font-bold mb-6">Rings</h1>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {products.map((product) => {
          const imageFailed = failedImages[product.id];
          return (
            <div
              key={product.id}
              className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden border border-stone-200"
            >
              <div className="aspect-square overflow-hidden bg-stone-100">
                {!imageFailed ? (
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    onError={() => handleImageError(product.id)}
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full bg-amber-100 flex items-center justify-center">
                    <span className="text-stone-800 text-xs text-center px-1">{product.name}</span>
                  </div>
                )}
              </div>
              <div className="p-2">
                <h3 className="text-sm font-semibold text-stone-800 mb-1 truncate">{product.name}</h3>
                <button
                  onClick={() => handleViewDetails(product)}
                  className="w-full py-1.5 text-xs border border-amber-500 text-amber-600 rounded hover:bg-amber-500 hover:text-white transition-colors duration-300 font-medium"
                >
                  View Details
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {selectedProduct && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div
            ref={modalRef}
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
          >
            <div className="relative">
              <button
                onClick={() => setSelectedProduct(null)}
                className="absolute top-4 right-4 text-stone-500 hover:text-stone-800 transition-colors z-10"
              >
                <FaTimes size={24} />
              </button>
              <div className="p-6">
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="md:w-1/2">
                    <img
                      src={selectedProduct.image}
                      alt={selectedProduct.name}
                      className="w-full h-auto object-cover rounded-xl shadow-md"
                    />
                  </div>
                  <div className="md:w-1/2">
                    <h2 className="text-2xl font-bold text-stone-800 mb-2">{selectedProduct.name}</h2>
                    <p className="text-stone-600 mb-4">{selectedProduct.description}</p>
                    <div className="space-y-2">
                      <p className="text-sm text-stone-500">
                        <span className="font-medium">Material:</span> {selectedProduct.material}
                      </p>
                      <p className="text-sm text-stone-500">
                        <span className="font-medium">Craftsmanship:</span> {selectedProduct.craftsmanship}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Rings;